import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const PLACEHOLDERS = ["{{candidate_name}}", "{{candidate_first_name}}", "{{candidate_role}}", "{{candidate_company}}", "{{candidate_location}}", "{{form_link}}"];

const emptyForm = { name: "", subject: "", body: "" };

const TemplateManager = () => {
    const navigate = useNavigate();
    const orgId = localStorage.getItem("current_org_id");
    const recruiterId = localStorage.getItem("org_admin_id");

    const [templates, setTemplates] = useState([]);
    const [globalTemplates, setGlobalTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);    // null | "new" | template object
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([
            axios.get(`${SOURCING_URL}/email/templates`, { params: { org_id: orgId, created_by: recruiterId } }),
            axios.get(`${SOURCING_URL}/email/global-templates`),
        ])
            .then(([mine, global]) => {
                setTemplates(mine.data);
                setGlobalTemplates(global.data);
            })
            .catch(() => setError("Failed to load templates."))
            .finally(() => setLoading(false));
    }, [orgId, recruiterId]);

    const openNew = (base = emptyForm) => {
        setForm({ name: base.name ? `Copy of ${base.name}` : "", subject: base.subject || "", body: base.body || "" });
        setEditing("new");
        setError(null);
    };

    const openEdit = (tmpl) => {
        setForm({ name: tmpl.name, subject: tmpl.subject, body: tmpl.body });
        setEditing(tmpl);
        setError(null);
    };

    const handleSave = async () => {
        if (!form.name || !form.subject || !form.body) { setError("All fields are required."); return; }
        setSaving(true);
        setError(null);
        try {
            if (editing === "new") {
                const { data } = await axios.post(`${SOURCING_URL}/email/global-templates`, {
                    name: form.name, subject: form.subject, body: form.body,
                });
                setGlobalTemplates(prev => [data, ...prev]);
            } else {
                const { data } = await axios.put(`${SOURCING_URL}/email/templates/${editing.id}`, form);
                setTemplates(prev => prev.map(t => t.id === data.id ? data : t));
            }
            setEditing(null);
        } catch (e) {
            setError(e.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async (id) => {
        if (!window.confirm("Archive this template?")) return;
        await axios.delete(`${SOURCING_URL}/email/templates/${id}`);
        setTemplates(prev => prev.filter(t => t.id !== id));
    };

    const insertPlaceholder = (ph) => {
        setForm(f => ({ ...f, body: f.body + ph }));
    };

    if (editing) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-white/[0.03]">
                <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-4">
                    <button onClick={() => setEditing(null)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-base font-semibold text-gray-800 dark:text-white">
                        {editing === "new" ? "New Template" : "Edit Template"}
                    </h1>
                </div>
                <div className="max-w-2xl mx-auto px-6 py-8 flex flex-col gap-5">
                    <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Template Name</label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-100"
                            placeholder="e.g. Initial Outreach"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Subject Line</label>
                        <input
                            type="text"
                            value={form.subject}
                            onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                            className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-100"
                            placeholder="e.g. Exciting opportunity at our company, {{candidate_name}}"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-gray-500 dark:text-gray-400">Email Body</label>
                            <div className="flex items-center gap-1 flex-wrap justify-end">
                                {PLACEHOLDERS.map(ph => (
                                    <button
                                        key={ph}
                                        onClick={() => insertPlaceholder(ph)}
                                        className="text-xs px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-300 hover:bg-purple-100 transition-all"
                                    >
                                        {ph}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <textarea
                            rows={10}
                            value={form.body}
                            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                            className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-100 resize-none"
                            placeholder="Hi {{candidate_first_name}}, ..."
                        />
                    </div>

                    {error && <p className="text-xs text-red-500 dark:text-red-400">{error}</p>}

                    <div className="flex gap-3">
                        <button onClick={() => setEditing(null)} className="px-5 py-2 rounded-full text-sm border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500">
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-5 py-2 rounded-full text-sm font-medium text-white disabled:opacity-50"
                            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                        >
                            {saving ? "Saving..." : "Save Template"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-white/[0.03]">
            <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-base font-semibold text-gray-800 dark:text-white flex-1">Email Templates</h1>
                <button
                    onClick={() => openNew()}
                    className="px-4 py-1.5 rounded-full text-sm font-medium text-white"
                    style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                >
                    + New Template
                </button>
            </div>

            <div className="max-w-3xl mx-auto px-6 py-8 flex flex-col gap-8">
                {loading ? (
                    <div className="flex justify-center py-16">
                        <div className="w-7 h-7 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* My templates */}
                        {/* <section>
                            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">My Templates ({templates.length})</h2>
                            {templates.length === 0 ? (
                                <p className="text-sm text-gray-400 dark:text-gray-500">No templates yet. Create one or start from a global template below.</p>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    {templates.map(t => (
                                        <div key={t.id} className="bg-white dark:bg-white/5 rounded-xl border border-gray-200 dark:border-gray-600 px-5 py-4 flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-800 dark:text-white">{t.name}</p>
                                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{t.subject}</p>
                                            </div>
                                            <div className="flex gap-2 shrink-0">
                                                <button onClick={() => openEdit(t)} className="text-xs text-purple-600 dark:text-purple-300 hover:underline">Edit</button>
                                                <button onClick={() => handleArchive(t.id)} className="text-xs text-red-400 hover:underline">Archive</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section> */}

                        {/* Global templates */}
                        {globalTemplates.length > 0 && (
                            <section>
                                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Platform Templates</h2>
                                <div className="flex flex-col gap-3">
                                    {globalTemplates.map(t => (
                                        <div key={t.id} className="bg-white dark:bg-white/5 rounded-xl border border-gray-200 dark:border-gray-600 px-5 py-4 flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-medium text-gray-800 dark:text-white">{t.name}</p>
                                                    {t.category && (
                                                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/15 text-blue-500">{t.category}</span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{t.subject}</p>
                                            </div>
                                            <button onClick={() => openNew(t)} className="text-xs text-purple-600 dark:text-purple-300 hover:underline shrink-0">
                                                Use as base
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TemplateManager;